import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Button } from 'components/ui';
import Select from 'react-select';
import { ArrowLeftIcon, DocumentChartBarIcon, ChevronDownIcon, BanknotesIcon, CreditCardIcon, QrCodeIcon, GlobeAltIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { Accordion, AccordionItem, AccordionButton, AccordionPanel } from 'components/ui/Accordion';
import { Switch } from '@headlessui/react';
import clsx from 'clsx';
import { getCurrencyLabel, getCurrencySymbol } from 'utils/paymentFormatters.jsx';
import { getPaymentTypes } from 'services/paymentServices';
import { useAutoAnimate } from '@formkit/auto-animate/react';

const getPaymentIcon = (type) => {
  const name = (type || '').toLowerCase();
  if (name.includes('cash')) return <BanknotesIcon className="size-7" />;
  if (name.includes('card')) return <CreditCardIcon className="size-7" />;
  if (name.includes('upi') || name.includes('qr')) return <QrCodeIcon className="size-7" />;
  return <GlobeAltIcon className="size-7" />;
};

export const PaymentDetailsForm = ({
  selectedGateway,
  paymentCatalogs,
  isLoadingCatalogs,
  selectedCatalog,
  applicationForms = [],
  isLoadingApplicationForms = false,
  selectedApplicationForm = null,
  setSelectedApplicationForm,
  paymentFormData,
  isCreatingLink,
  onSelectCatalog,
  onInputChange,
  onBack,
  onCreate,
  showActions = true,
  isFeePayment = false,
  isInternational = false,
  feeBreakup = null,
  leadData = null,
}) => {
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [isLoadingPaymentTypes, setIsLoadingPaymentTypes] = useState(false);
  const partialAmountRef = useRef(null);
  const discountAmountRef = useRef(null);
  const [touched, setTouched] = useState({});
  const [discountParent] = useAutoAnimate();
  const [paymentModeParent] = useAutoAnimate();

  const academicAmount = parseFloat((feeBreakup?.academic?.amount || 0).toString().replace(/,/g, '')) || 0;
  const ptuFee = parseFloat((feeBreakup?.academic?.ptuFee || 0).toString().replace(/,/g, '')) || 0;
  const kitFee = parseFloat((feeBreakup?.academic?.kitFee || 0).toString().replace(/,/g, '')) || 0;
  const hostelAmount = parseFloat((feeBreakup?.hostel?.amount || 0).toString().replace(/,/g, '')) || 0;
  const totalBaseFee = academicAmount + hostelAmount + ptuFee + kitFee;

  const discountValue = parseFloat((paymentFormData.DiscountValue || 0).toString().replace(/,/g, '')) || 0;
  let discountAmount = 0;
  if (paymentFormData.IsDiscountApplied) {
    if (paymentFormData.IsDiscountTypePercentage) {
      // Discount applies strictly to Academic/Course Fee only
      discountAmount = (academicAmount * discountValue) / 100;
    } else {
      discountAmount = discountValue;
    }
  }

  const netPayable = totalBaseFee - discountAmount;

  // Logic for "Fully Paid" toggle
  const isFullyPaid = !paymentFormData.IsPartialPaymentMade;
  const payingNow = isFullyPaid ? netPayable : parseFloat(paymentFormData.amount || 0);
  const balance = netPayable - payingNow;


  // Handle Discount Type Switch - Clear Value
  const handleDiscountTypeChange = (isPercentage) => {
    onInputChange({ target: { name: 'IsDiscountTypePercentage', value: isPercentage } });
    onInputChange({ target: { name: 'DiscountValue', value: 0 } });
    setTouched(prev => ({ ...prev, DiscountValue: false }));
    setTimeout(() => {
      discountAmountRef.current?.focus();
    }, 0);
  };

  const currencySymbol = isInternational ? '$' : '₹';
  const currencyLabel = isInternational ? 'USD' : 'INR';

  // Synchronous validation calculation
  const validationErrors = {};
  if (paymentFormData.IsDiscountApplied && discountValue > 0) {
    if (paymentFormData.IsDiscountTypePercentage) {
      if (discountValue > 100) {
        validationErrors.discount = "Percentage cannot exceed 100%";
      }
    } else {
      const discountable = academicAmount;
      if (discountValue > discountable) {
        validationErrors.discount = `Amount cannot exceed ${currencySymbol}${discountable.toLocaleString()}`;
      }
    }
  }

  if (paymentFormData.IsPartialPaymentMade) {
    const partialStr = (paymentFormData.amount || "").toString().replace(/,/g, '');
    const partial = parseFloat(partialStr || 0);
    const minRequired = hostelAmount;
    
    if (partial < minRequired) {
      validationErrors.partial = `Minimum ${currencySymbol}${minRequired.toLocaleString()} required (Full Hostel Fee)`;
    } else if (partial > Math.round(netPayable)) {
      validationErrors.partial = `Cannot exceed ${currencySymbol}${netPayable.toLocaleString()}`;
    } else if (isNaN(partial) || (partial <= 0 && minRequired === 0)) {
      validationErrors.partial = "Enter a valid amount";
    }
  }

  const hasValidationError = Object.keys(validationErrors).length > 0;
  
  const partialVal = parseFloat((paymentFormData.amount || "").toString().replace(/,/g, '')) || 0;
  // Check if amount is too low (less than hostel) or too high (more than net payable)
  const isPartialAmountInvalid = paymentFormData.IsPartialPaymentMade && (partialVal < hostelAmount || partialVal > Math.round(netPayable));

  const isSubmitDisabled = isCreatingLink ||
    hasValidationError ||
    isPartialAmountInvalid ||
    (!isFeePayment && !selectedCatalog) ||
    !paymentFormData.amount ||
    (paymentFormData.amount === '0' && isFeePayment) ||
    (selectedGateway?.GatewayCode === 'MAN' && (
      !paymentFormData.paymentMethod || 
      (paymentFormData.paymentMethod !== 'cash' && !paymentFormData.transactionId)
    ));

  const getButtonText = () => {
    if (isCreatingLink) return selectedGateway?.GatewayCode === 'MAN' ? 'Saving...' : 'Creating...';
    if (validationErrors.partial || isPartialAmountInvalid) {
       if (partialVal < hostelAmount) return 'Min. Amount Required';
       if (partialVal > Math.round(netPayable)) return 'Amount Exceeded';
       return 'Invalid Amount';
    }
    if (validationErrors.discount) return 'Fix Discount Error';
    if (!paymentFormData.amount) return 'Enter Amount';
    if (!isFeePayment && !selectedCatalog) return 'Select Catalog';
    if (selectedGateway?.GatewayCode === 'MAN') {
      if (!paymentFormData.paymentMethod) return 'Select Method';
      if (paymentFormData.paymentMethod !== 'cash' && !paymentFormData.transactionId) return 'Enter Txn ID';
      return 'Save Payment';
    }
    return 'Create Link';
  };

  // Sync amount with netPayable when in Full Payment mode
  useEffect(() => {
    if (isFeePayment && isFullyPaid) {
      const netPayableStr = Math.max(0, netPayable).toString();
      if (paymentFormData.amount !== netPayableStr) {
        onInputChange({ target: { name: 'amount', value: netPayableStr } });
      }
    }
  }, [isFeePayment, isFullyPaid, netPayable, paymentFormData.amount, onInputChange]);

  useEffect(() => {
    const fetchPaymentTypes = async () => {
      setIsLoadingPaymentTypes(true);
      const res = await getPaymentTypes();
      if (res?.success) {
        setPaymentTypes(res.data || []);
      }
      setIsLoadingPaymentTypes(false);
    };

    if (selectedGateway?.GatewayCode === 'MAN') {
      fetchPaymentTypes();
    }
  }, [selectedGateway]);

  const handleEditClick = () => {
    if (isFullyPaid) {
      onInputChange({ target: { name: 'IsPartialPaymentMade', value: true } });
    }
    setTimeout(() => {
      partialAmountRef.current?.focus();
    }, 0);
  };


  const paymentMethodOptions = useMemo(() => {
    return paymentTypes.map(pt => ({
      value: pt.Code.toLowerCase(),
      label: pt.PaymentName,
      originalCode: pt.Code
    }));
  }, [paymentTypes]);

  const paymentCatalogOptions = useMemo(
    () =>
      (paymentCatalogs || []).map((catalog) => ({
        value: catalog,
        label: catalog.AmountName,
      })),
    [paymentCatalogs]
  );

  const selectedCatalogOption = useMemo(
    () =>
      selectedCatalog
        ? {
          value: selectedCatalog,
          label: selectedCatalog.AmountName,
        }
        : null,
    [selectedCatalog]
  );


  return (
    <>
      <div className="flex flex-col">
        <div className="flex-1 space-y-2">
          {isFeePayment ? (
            <>
              {/* Compact Header Summary */}
              <div className="p-2 rounded-xl border border-gray-100 bg-gray-50/50 dark:border-dark-700 dark:bg-dark-900/50 space-y-1">
                <div className="flex justify-between items-center px-1">
                  <span className="text-[11px] font-semibold text-gray-400">Course Fee</span>
                  <span className="text-[13px] font-bold text-gray-700 dark:text-dark-200">{currencySymbol}{academicAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center px-1">
                  <span className="text-[11px] font-semibold text-gray-400">Hostel Fee</span>
                  <span className="text-[13px] font-bold text-gray-700 dark:text-dark-200">{currencySymbol}{hostelAmount.toLocaleString()}</span>
                </div>
                {ptuFee > 0 && (
                  <div className="flex justify-between items-center px-1">
                    <span className="text-[11px] font-semibold text-gray-400">PTU Counseling Fee</span>
                    <span className="text-[13px] font-bold text-gray-700 dark:text-dark-200">{currencySymbol}{ptuFee.toLocaleString()}</span>
                  </div>
                )}
                {kitFee > 0 && (
                  <div className="flex justify-between items-center px-1">
                    <span className="text-[11px] font-semibold text-gray-400">Kit Charges</span>
                    <span className="text-[13px] font-bold text-gray-700 dark:text-dark-200">{currencySymbol}{kitFee.toLocaleString()}</span>
                  </div>
                )}
                <div className="pt-1 border-t border-gray-200 dark:border-dark-700 flex justify-between items-center px-1">
                  <span className="text-[12px] font-bold text-gray-500">Total Fee</span>
                  <span className="text-[13px] font-black text-gray-800 dark:text-dark-100">{currencySymbol}{totalBaseFee.toLocaleString()}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between items-center px-1 animate-in fade-in slide-in-from-top-1">
                    <span className="text-[11px] font-semibold text-emerald-600/80">Discount (on Course Fee)</span>
                    <span className="text-[13px] font-bold text-emerald-600">-{currencySymbol}{discountAmount.toLocaleString()}</span>
                  </div>
                )}
                {paymentFormData.IsPartialPaymentMade && (
                  <div className="flex justify-between items-center px-2 py-0.5 bg-gray-50/50 dark:bg-dark-800/30 rounded-md border border-gray-100 dark:border-dark-700/50 mt-0.5">
                    <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                      {discountAmount > 0 ? 'Total after Discount' : 'Total Amount'}
                    </span>
                    <span className="text-[11px] font-bold text-gray-600 dark:text-dark-300">
                      {currencySymbol}{(academicAmount + hostelAmount + ptuFee + kitFee - discountAmount).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Discount Section - Ultra Compact */}
              <div ref={discountParent} className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[12px] font-semibold text-gray-400">
                    Discount
                  </label>
                  <Switch
                    checked={paymentFormData.IsDiscountApplied}
                    onChange={(checked) => {
                      onInputChange({ target: { name: 'IsDiscountApplied', value: checked } });
                      if (checked) {
                        setTimeout(() => {
                          discountAmountRef.current?.focus();
                        }, 0);
                      } else {
                        // Clear discount values when disabled to prevent stale data being sent
                        onInputChange({ target: { name: 'DiscountValue', value: 0 } });
                        onInputChange({ target: { name: 'IsDiscountTypePercentage', value: false } });
                      }
                    }}
                    className={clsx(
                      paymentFormData.IsDiscountApplied ? 'bg-primary-600' : 'bg-gray-200 dark:bg-dark-600',
                      'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none'
                    )}
                  >
                    <span
                      className={clsx(
                        paymentFormData.IsDiscountApplied ? 'translate-x-5' : 'translate-x-1',
                        'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform'
                      )}
                    />
                  </Switch>
                </div>

                {paymentFormData.IsDiscountApplied && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                    <div className="flex items-center justify-between p-1 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30">
                      <div className="flex bg-blue-100/30 dark:bg-dark-800 p-0.5 rounded-lg gap-0.5 w-[260px]">
                        <button
                          type="button"
                          onClick={() => handleDiscountTypeChange(false)}
                          className={clsx(
                            "flex-1 px-4 py-1.5 rounded-md text-[11px] font-bold uppercase transition-all whitespace-nowrap",
                            !paymentFormData.IsDiscountTypePercentage
                              ? "bg-white text-primary-600 shadow-sm dark:bg-dark-700 dark:text-primary-400"
                              : "text-blue-400 hover:text-blue-600 dark:text-blue-400/60"
                          )}
                        >
                          Flat
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDiscountTypeChange(true)}
                          className={clsx(
                            "flex-1 px-4 py-1.5 rounded-md text-[11px] font-bold uppercase transition-all whitespace-nowrap",
                            paymentFormData.IsDiscountTypePercentage
                              ? "bg-white text-primary-600 shadow-sm dark:bg-dark-700 dark:text-primary-400"
                              : "text-blue-400 hover:text-blue-600 dark:text-blue-400/60"
                          )}
                        >
                          Percentage
                        </button>
                      </div>

                      <div className="flex items-center gap-1 px-3">
                        {!paymentFormData.IsDiscountTypePercentage && (
                          <span className="text-xs font-bold text-blue-400">{currencySymbol}</span>
                        )}
                        <input
                          ref={discountAmountRef}
                          type="text"
                          inputMode="numeric"
                          name="DiscountValue"
                          value={paymentFormData.DiscountValue ? Number(paymentFormData.DiscountValue).toLocaleString() : ''}
                          onChange={(e) => {
                            let val = e.target.value.replace(/,/g, '').replace(/[^0-9.]/g, '');
                            if (val.split('.').length > 2) return;
                            
                            // Prevent percentage from exceeding 100
                            if (paymentFormData.IsDiscountTypePercentage && parseFloat(val) > 100) {
                              val = '100';
                            }
                            
                            onInputChange({ target: { name: 'DiscountValue', value: val } });
                          }}
                          onBlur={() => setTouched(prev => ({ ...prev, DiscountValue: true }))}
                          placeholder="0"
                          className={clsx(
                            "w-16 bg-transparent text-sm font-black text-right focus:outline-none transition-all",
                            (validationErrors.discount) ? "text-red-500" : "text-primary-700 dark:text-primary-400"
                          )}
                        />
                        {paymentFormData.IsDiscountTypePercentage && (
                          <span className="text-xs font-bold text-blue-400">%</span>
                        )}
                      </div>
                    </div>
                    {validationErrors.discount && (
                      <p className="text-[9px] text-red-500 font-bold px-2">{validationErrors.discount}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Fully Paid Blue Bar & Net Payable */}
              <div ref={paymentModeParent} className="space-y-2">
                <div className="p-1 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30">
                  <div className="flex items-center justify-between">
                    <div className="flex bg-blue-100/30 dark:bg-dark-800 p-0.5 rounded-lg gap-0.5 w-[260px]">
                      <button
                        type="button"
                        onClick={() => {
                          onInputChange({ target: { name: 'IsPartialPaymentMade', value: false } });
                          onInputChange({ target: { name: 'amount', value: netPayable.toString() } });
                        }}
                        className={clsx(
                          "flex-1 px-4 py-1.5 rounded-md text-[12px] font-semibold transition-all whitespace-nowrap",
                          isFullyPaid
                            ? "bg-white text-primary-600 shadow-sm dark:bg-dark-700 dark:text-primary-400"
                            : "text-blue-400 hover:text-blue-600 dark:text-blue-400/60"
                        )}
                      >
                        Full Payment
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          onInputChange({ target: { name: 'IsPartialPaymentMade', value: true } });
                          onInputChange({ target: { name: 'amount', value: '' } });
                          setTimeout(() => {
                            partialAmountRef.current?.focus();
                          }, 0);
                        }}
                        className={clsx(
                          "flex-1 px-4 py-1.5 rounded-md text-[12px] font-semibold transition-all whitespace-nowrap",
                          !isFullyPaid
                            ? "bg-white text-blue-600 shadow-sm dark:bg-dark-700 dark:text-blue-400"
                            : "text-blue-400 hover:text-blue-600 dark:text-blue-400/60"
                        )}
                      >
                        Partial Payment
                      </button>
                    </div>

                    <div className="flex items-center gap-1.5 px-2">
                      <span className="text-xs font-bold text-blue-400">{currencySymbol}</span>
                      <input
                        ref={partialAmountRef}
                        type="text"
                        inputMode="numeric"
                        name="amount"
                        value={paymentFormData.amount ? Number(paymentFormData.amount).toLocaleString() : ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
                          onInputChange({ target: { name: 'amount', value: val } });
                        }}
                        onBlur={() => setTouched(prev => ({ ...prev, amount: true }))}
                        readOnly={isFullyPaid}
                        placeholder={isFullyPaid ? "" : "0"}
                        className={clsx(
                          "w-20 bg-transparent text-sm font-black text-right focus:outline-none transition-all",
                          isFullyPaid ? "text-primary-700 cursor-default" : (validationErrors.partial ? "text-red-600 border-b-2 border-red-500" : "text-blue-700 border-b-2 border-blue-200 focus:border-blue-500")
                        )}
                      />
                    </div>
                  </div>

                  {/* Parallel Validation and Balance Row */}
                  {!isFullyPaid && (validationErrors.partial || balance > 0) && (
                    <div className="flex items-start justify-between px-2 mt-1 animate-in fade-in slide-in-from-top-1 duration-200">
                      <div>
                        {validationErrors.partial && (
                          <p className="text-[10px] text-red-500 font-bold">{validationErrors.partial}</p>
                        )}
                      </div>
                      <div>
                        {balance > 0 && (
                          <span className="text-[11px] font-semibold text-amber-600 whitespace-nowrap">
                            Remaining Balance: {currencySymbol}{balance.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </>
          ) : (
            <>
              {/* Payment Catalog Selection - Standard Payment */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Payment Catalog <span className="text-red-500">*</span>
                </label>
                <Select
                  options={paymentCatalogOptions}
                  value={selectedCatalogOption}
                  onChange={onSelectCatalog}
                  isLoading={isLoadingCatalogs}
                  placeholder="Select a payment catalog..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={isCreatingLink}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </div>

              {/* Amount - Standard Payment */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Total Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-sm font-bold text-gray-500 dark:text-dark-400">{currencySymbol}</span>
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    name="amount"
                    value={paymentFormData.amount}
                    onChange={onInputChange}
                    placeholder="Enter amount"
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 pl-7 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 cursor-not-allowed"
                    disabled={true}
                    required
                  />
                </div>
              </div>

              {/* Currency - Standard Payment */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Currency <span className="text-red-500">*</span>
                </label>
                <select
                  name="currency"
                  value={paymentFormData.currency}
                  onChange={onInputChange}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  disabled={isCreatingLink || !!selectedCatalog?.Currency}
                >
                  <option value="INR">{getCurrencyLabel('INR')}</option>
                  <option value="USD">{getCurrencyLabel('USD')}</option>
                </select>
              </div>
            </>
          )}

          {/* Manual Gateway Fields */}
          {selectedGateway?.GatewayCode === 'MAN' && (
            <div className="space-y-2">
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-gray-400">
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-5 gap-1">
                  {paymentTypes.map((type) => {
                    const methodValue = type.Code.toLowerCase();
                    const isActive = paymentFormData.paymentMethod === methodValue;
                    return (
                      <button
                        key={type.Id || methodValue}
                        type="button"
                        onClick={() => onInputChange({ target: { name: 'paymentMethod', value: methodValue } })}
                        className={clsx(
                          "flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl border transition-all",
                          isActive
                            ? "border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 shadow-sm"
                            : "border-gray-200 bg-gray-50/50 text-gray-600 hover:border-gray-300 hover:text-gray-800 dark:border-dark-700 dark:bg-dark-900 dark:text-dark-300"
                        )}
                      >
                        <div className={clsx("transition-transform", isActive && "scale-110")}>
                          {getPaymentIcon(type.PaymentName)}
                        </div>
                        <span className="text-[11px] font-semibold whitespace-normal leading-[1.1] w-full text-center">{type.PaymentName}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-[12px] font-semibold text-gray-400">
                  Transaction ID {paymentFormData.paymentMethod !== 'cash' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  name="transactionId"
                  value={paymentFormData.transactionId}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 20);
                    onInputChange({ target: { name: 'transactionId', value } });
                  }}
                  placeholder="Enter transaction ID"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-700 dark:bg-dark-900"
                  disabled={isCreatingLink}
                  required={paymentFormData.paymentMethod !== 'cash'}
                />
              </div>
            </div>
          )}

          {/* Application Form & Description - Hidden for Fee Payment */}
          {!isFeePayment && (
            <>
              {/* Application Form Selection */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Application Form <span className="text-red-500">*</span>
                </label>
                <Select
                  options={(applicationForms || []).map(form => ({ value: form, label: form.Title || form.FormName }))}
                  value={selectedApplicationForm ? { value: selectedApplicationForm, label: selectedApplicationForm.Title || selectedApplicationForm.FormName } : null}
                  onChange={(option) => setSelectedApplicationForm(option?.value || null)}
                  isLoading={isLoadingApplicationForms}
                  placeholder="Select an application form..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={isCreatingLink}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </div>

              {/* Description */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={paymentFormData.description}
                  onChange={onInputChange}
                  placeholder="Enter payment description"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 min-h-[80px]"
                  disabled={isCreatingLink}
                  required
                />
              </div>
            </>
          )}
        </div>
      </div>
      {/* Actions */}
      {showActions && (
        <div className="mt-3 flex gap-3">
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={isCreatingLink}
            className="flex-1"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span className="ml-1">Back</span>
          </Button>
          <Button
            variant="solid"
            color={isSubmitDisabled ? "neutral" : "primary"}
            onClick={() => {
              if (isSubmitDisabled) return;
              setTouched({
                DiscountValue: true,
                amount: true,
                paymentMethod: true,
                transactionId: true
              });
              onCreate();
            }}
            disabled={isSubmitDisabled}
            className={clsx("flex-1", isSubmitDisabled && "opacity-60 cursor-not-allowed")}
          >
            {getButtonText()}
          </Button>
        </div>
      )}
    </>
  );
};
